import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {Button, Image, message, Space, Spin, Tag, Typography} from 'antd';
import {ClockCircleOutlined, FireOutlined, StarOutlined} from '@ant-design/icons';
import dayjs from 'dayjs';
import NewsService from '../../service/newsService';
import {ApiResponse, ClusterSummaryView, NewsArticle} from '../../types/news';
import ImageAttribution from './ImageAttribution';
import {useAuth} from '../../contexts/AuthContext';
import ReadLaterService from '../../service/readLaterService';
import {decodeHtmlEntities} from '../../utils/htmlUtils';
import {USE_USER} from '../../config/features';
import {parseTags, prioritizeTags} from '../../utils/tagUtils';
import '../../styles/NewsDetail.css';

const { Title, Text } = Typography;

const NewsDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { token } = useAuth();
    const [article, setArticle] = useState<NewsArticle | null>(null);
    const [clusterSummary, setClusterSummary] = useState<ClusterSummaryView | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [imageError, setImageError] = useState(false);
    const [isInReadLater, setIsInReadLater] = useState<boolean>(false);
    const [readLaterLoading, setReadLaterLoading] = useState<boolean>(false);

    useEffect(() => {
        const fetchArticle = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response: ApiResponse<NewsArticle> = await NewsService.getNewsById(id);
                const articleData = response.data;
                setArticle(articleData);

                // If article has a cluster_id, fetch the cluster summary
                if (articleData.cluster_id) {
                    try {
                        const clusterResponse = await NewsService.getClusterSummary(articleData.cluster_id);
                        setClusterSummary(clusterResponse.data);
                    } catch (clusterErr: any) {
                        // Continue without cluster summary - non-critical failure
                    }
                }
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
            
        };
        fetchArticle();
    }, [id]);

    const checkReadLaterStatus = useCallback(async () => {
        try {
            const response = await ReadLaterService.checkReadLaterStatus(article!.id);
            setIsInReadLater(response.is_in_read_later);
        } catch (error) {
            // Silently handle error, don't show to user
        }
    }, [article]);

    // Check if article is in read later
    useEffect(() => {
        if (USE_USER && token && article?.id) {
            checkReadLaterStatus();
        }
    }, [token, article?.id, checkReadLaterStatus]);

    const handleReadLaterClick = async () => {
        if (!token) {
            message.warning('Please log in to save articles for later reading');
            return;
        }

        if (!article) return;

        setReadLaterLoading(true);
        try {
            if (isInReadLater) {
                await ReadLaterService.removeFromReadLater(article.id);
                setIsInReadLater(false);
                message.success('Removed from read later');
            } else {
                await ReadLaterService.addToReadLater({
                    article_id: article.id,
                    cluster_id: article.cluster_id,
                });
                setIsInReadLater(true);
                message.success('Added to read later');
            }
        } catch (error: any) {
            // Handle "already in read later" error gracefully
            if (error.message && error.message.includes('already in read later')) {
                setIsInReadLater(true);
                message.info('Article is already in your read later list');
            } else {
                message.error(error.message || 'Failed to update read later');
            }
        } finally {
            setReadLaterLoading(false);
        }
    };

    const handleBack = () => {
        const fromPath = location.state?.from;
        
        if (fromPath) {
            navigate(fromPath);
        } else {
            try {
                navigate(-1);
            } catch {
                navigate('/');
            }
        }
    };

    if (loading) {
        return <Spin spinning style={{ display: 'block', textAlign: 'center', margin: '50px 0' }} />;
    }

    if (error || !article) {
        return (
            <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
                <Text type="danger">{error || 'Article not found'}</Text>
                <Button onClick={handleBack} style={{ marginLeft: '16px' }}>
                    Back to List
                </Button>
            </div>
        );
    }

    const tags = prioritizeTags(parseTags(article.tags_json));

    // Use cluster summary if available, otherwise fall back to article content
    const contentToDisplay = clusterSummary?.cluster_summary || article.content || 'No content available.';

    return (
        <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto', position: 'relative', minHeight: '100vh' }}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space align="center" wrap>
                    <Title 
                        level={2} 
                        style={{ 
                            margin: 0,
                            color: article.breaking_rating && article.breaking_rating >= 4 ? '#cf1322' : undefined
                        }}
                    >
                        {article.breaking_headline || article.title || 'No Title'}
                    </Title>
                    {article.breaking_rating && article.breaking_rating >= 4 && (
                        <Tag 
                            color="red" 
                            icon={<FireOutlined />}
                            style={{ 
                                fontSize: '14px',
                                padding: '4px 12px',
                                fontWeight: 'bold'
                            }}
                        >
                            BREAKING NEWS
                        </Tag>
                    )}
                    {/* Read Later Button */}
                    {USE_USER && token && (
                        <Button
                            type={isInReadLater ? 'primary' : 'default'}
                            icon={<ClockCircleOutlined />}
                            loading={readLaterLoading}
                            onClick={handleReadLaterClick}
                            style={{
                                marginLeft: 'auto',
                                backgroundColor: isInReadLater ? '#52c41a' : undefined,
                                borderColor: isInReadLater ? '#52c41a' : undefined
                            }}
                        >
                            {isInReadLater ? 'Saved' : 'Read Later'}
                        </Button>
                    )}
                </Space>
                
                {/* Breaking news rating */}
                {article.breaking_rating && article.breaking_rating >= 4 && (
                    <Space>
                        <Tag 
                            color="red" 
                            icon={<StarOutlined />}
                            style={{ fontSize: '12px' }}
                        >
                            Rating: {article.breaking_rating}/5
                        </Tag>
                    </Space>
                )}
            </Space>
            
            <Space style={{ marginBottom: '16px' }}>
                <Text type="secondary">
                    By {article.author || 'Unknown'} |{' '}
                    {article.publication_date ? dayjs(article.publication_date).format('YYYY-MM-DD') : 'N/A'}
                </Text>
            </Space>
            
            {article.image_url && !imageError && (
                <div style={{ marginBottom: '16px' }}>
                    <Image
                        src={article.image_url}
                        alt={article.title || 'News Image'}
                        width="100%"
                        style={{ maxHeight: '400px', objectFit: 'cover', marginBottom: '8px' }}
                        fallback="https://via.placeholder.com/800x400"
                        onError={() => setImageError(true)}
                    />
                    <ImageAttribution article={article} />
                </div>
            )}
            <div 
                className="article-content"
                dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(contentToDisplay) }}
            />
            <Space wrap style={{ marginTop: '16px' }}>
                {tags.map((tag, index) => (
                    <Tag
                        key={index}
                        color="blue"
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/tags?tag=${encodeURIComponent(tag.name)}`)}
                    >
                        #{tag.name}
                    </Tag>
                ))}
            </Space>

            <Button
                onClick={handleBack}
                style={{
                    position: 'fixed',
                    right: '40px',
                    bottom: '70px',
                    zIndex: 1000
                }}
            >
                ← Back
            </Button>
        </div>
    );
};

export default NewsDetail;